import { useState, useEffect, useCallback, useDeferredValue } from 'react';
import { useTabEnter } from '@ai-tutor/ui';
import { LessonHeader } from './LessonHeader';
import { LessonBottomTabs } from './LessonBottomTabs';
import { LessonBuildProgress } from './LessonBuildProgress';
import { LessonScopeCheckpoint } from './LessonScopeCheckpoint';
import { MediaViewer } from '../media/MediaViewer';
import { NotesViewer } from '../notes/NotesViewer';
import { PracticeViewer } from '../practice/PracticeViewer';
import { ReferenceViewer } from '../sources/ReferenceViewer';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { useLessonTabParam } from '@/hooks/lesson/useLessonTabParam';
import { calculateLessonProgress } from '@/utils/lessonProgress';
import { mockLessonData, type LessonData, type Source } from '@/data/fixtures';
import type { LessonTab, LessonStyle, LessonProgress } from '@/types/lesson';
import type { SourceItem } from '@/lib/api-client';
import type { StepView, SourcesView } from '@/types/pipeline';

/** Human-readable name per tab for the in-panel "X panel failed" copy. */
const TABS: readonly LessonTab[] = ['lesson', 'notes', 'practice', 'reference'];

const TAB_LABEL: Record<LessonTab, string> = {
  lesson: 'Lesson',
  notes: 'Notes',
  practice: 'Practice',
  reference: 'Appendix',
};

interface LessonViewerProps {
  lessonId?: string;
  currentVersion?: string;
  onVersionChange?: (version: string) => void;
  selectedSource?: Source | null;
  /**
   * Per-step view map threaded down from `LessonLearningLayout`. Each
   * sub-viewer (MediaViewer / PracticeViewer / ReferenceViewer) reads its
   * relevant keys to decide skeleton/loaded/failed/generate. NotesViewer
   * doesn't need this - notes are user-created, not pipeline-driven.
   */
  steps?: Record<string, StepView>;
  /** Source-ingestion rollup - drives the build view's first stage. */
  sources?: SourcesView;
  /** Whether the user supplied sources - labels the build view's source step. */
  hasUserSources?: boolean;
  /** The title the build view shows (omitted where the layout shows it). */
  lessonTitle?: string;
  /** Lesson-level lifecycle terminal flag - used by off-flow gating. */
  lessonDone?: boolean;
  /**
   * True once the page has both lesson metadata + pipeline-status
   * snapshot loaded. While false, every downstream `<StepGate>`
   * renders a neutral "Loading X" skeleton.
   */
  lessonReady?: boolean;
  /** Refetch pipeline-status (passed to the build view's retry). */
  onRetry?: () => void;
  /**
   * A large source waiting for the user to pick which chapters to ingest.
   * When present, the center panel shows the scope checkpoint instead of the
   * build/loading view — nothing is processing until the user decides.
   */
  scopeSource?: SourceItem | null;
  /** How many large sources are still awaiting chapter selection (>=1 when
   *  `scopeSource` is set) — surfaced as a "N documents" counter. */
  scopePendingCount?: number;
  /** Reopen the chapter-selection dialog from the checkpoint / banner. */
  onOpenScope?: () => void;
  /**
   * Where the four lesson tabs go. `top` (desktop) is the header row with the
   * Progress menu beside it. `bottom` (phone and tablet) is a thumb-reach
   * bar under the content, and the Progress menu moves to the page's top bar
   * (`LessonTopBar`), so this viewer draws no header at all.
   */
  tabPlacement?: 'top' | 'bottom';
}

export function LessonViewer({
  lessonId,
  selectedSource,
  steps,
  sources,
  hasUserSources,
  lessonTitle,
  lessonDone,
  lessonReady,
  onRetry,
  scopeSource,
  scopePendingCount = 0,
  onOpenScope,
  tabPlacement = 'top',
}: LessonViewerProps) {
  // In the URL (`?tab=`), so a link or a reload opens the lesson on the tab
  // it names — see `useLessonTabParam`.
  const [activeTab, setActiveTab] = useLessonTabParam('tab', TABS, 'lesson');
  // The tab bar follows `activeTab` at once; the body follows this deferred
  // copy, so React commits the cheap tab-bar change (and its sliding pill)
  // before the expensive tab mount. Without it, clicking Notes held the pill
  // still for ~150ms while the editor mounted, and the slide was half skipped.
  const shownTab = useDeferredValue(activeTab);
  const tabBodyRef = useTabEnter<HTMLDivElement>(shownTab);

  // When the user picks a source from the SourcesPanel, jump to the Reference
  // tab so they actually see the SourceViewer (which only mounts inside it).
  useEffect(() => {
    if (selectedSource) setActiveTab('reference');
  }, [selectedSource]);
  // `lessonStyle` used to flip between Standard / ELI5 / Story / Revision
  // via the tab kebab menu. That switcher was retired; style is now
  // fixed at "standard" and the variable stays only because the tab
  // content components still take it as a prop. Inlined as a const.
  const lessonStyle: LessonStyle = 'standard';
  const [lessonData, setLessonData] = useState<LessonData>(mockLessonData);
  const [progress, setProgress] = useState<LessonProgress>({
    overall: 0,
    lesson: 0,
    notes: 0,
    practice: 0,
    reference: 0,
  });

  // Memoized callback for notes updates
  const handleNotesUpdate = useCallback(
    (aiNotes: number, personalNotes: number) => {
      setLessonData(prev => {
        // Bail out when nothing actually changed. The updater previously
        // returned a fresh object every call, so even a no-op notes update
        // re-rendered the whole viewer — which, paired with a caller that
        // fired on every render, was half of an infinite loop.
        if (
          prev.notesTaken === aiNotes &&
          prev.personalNotesCount === personalNotes
        ) {
          return prev;
        }
        return {
          ...prev,
          notesTaken: aiNotes,
          personalNotesCount: personalNotes,
        };
      });
    },
    []
  );

  // Calculate progress whenever lesson data changes
  // Removed dependency to prevent infinite loop
  // Progress will be calculated on mount and when specific actions update lessonData
  useEffect(() => {
    const newProgress = calculateLessonProgress(lessonData);
    setProgress(newProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderTabContent = () => {
    switch (shownTab) {
      case 'lesson':
        return (
          <MediaViewer
            lessonId={lessonId}
            lessonStyle={lessonStyle}
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            onPractice={() => setActiveTab('practice')}
            // Removed onProgressUpdate to prevent infinite loop
            // Progress tracking will be re-implemented with throttling if needed
          />
        );

      case 'notes':
        // Notes are user-created - no pipeline gating needed.
        return (
          <NotesViewer
            lessonId={lessonId}
            lessonStyle={lessonStyle}
            onNotesUpdate={handleNotesUpdate}
          />
        );

      case 'practice':
        return (
          <PracticeViewer
            lessonId={lessonId}
            lessonStyle={lessonStyle}
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            onProgressUpdate={(flashcards, quiz, attempts) => {
              setLessonData(prev => ({
                ...prev,
                flashcardsCompleted: flashcards,
                quizScore: quiz,
                quizAttempts: attempts,
              }));
            }}
          />
        );

      case 'reference':
        return (
          <ReferenceViewer
            lessonId={lessonId}
            selectedSource={selectedSource}
            steps={steps}
            lessonDone={lessonDone}
            lessonReady={lessonReady}
            onProgressUpdate={(sourcesViewed, glossaryViewed) => {
              setLessonData(prev => ({
                ...prev,
                sourcesViewed,
                glossaryViewed,
              }));
            }}
          />
        );

      default:
        return null;
    }
  };

  // Content tabs only make sense once the lesson context exists - every tab
  // depends on byte_selection's LessonContext. Until it's `completed` we show
  // the staged build view (no header, no tabs) instead of N empty skeletons,
  // and that view also hosts the byte_selection failure + retry. We only
  // switch to the build view once data has actually loaded (`lessonReady`),
  // so a revisited, already-complete lesson never flashes it.
  const contextReady = steps?.byte_selection?.state === 'completed';
  // A large source awaiting scope takes over the center ONLY during the initial
  // build (no lesson content yet). Once the viewed version has built content
  // (byte_selection completed), a newly-added big PDF must not blank out the
  // lesson — that case shows a slim banner in the main view instead (below).
  if (scopeSource && !contextReady) {
    return (
      <div className='flex h-full flex-col overflow-hidden'>
        <LessonScopeCheckpoint
          source={scopeSource}
          pendingCount={scopePendingCount}
          onChoose={onOpenScope}
        />
      </div>
    );
  }

  const showBuildView = lessonReady === true && !contextReady;

  if (showBuildView) {
    return (
      <div className='flex h-full flex-col overflow-hidden'>
        <LessonBuildProgress
          lessonId={lessonId}
          sources={sources}
          overview={steps?.overview}
          byteSelection={steps?.byte_selection}
          hasUserSources={hasUserSources}
          lessonTitle={lessonTitle}
          onRetry={onRetry}
        />
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      {tabPlacement === 'top' && (
        <LessonHeader
          progress={progress}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          hasNewContent={lessonData.hasNewContent}
          lessonId={lessonId}
        />
      )}

      <div ref={tabBodyRef} className='min-h-0 flex-1 overflow-hidden'>
        {/* Per-tab boundary so a crash in Practice / Notes / Appendix
            shows the in-panel "X panel failed" fallback (sidebar + chat
            stay alive) instead of bubbling up to PageBoundary's full-
            page "This page hit an error" view. Lesson sub-tabs already
            have an inner per-sub-tab boundary inside MediaViewer; this
            outer one is defense-in-depth and matches the same UX shape
            for any escapes. `key={activeTab}` remounts the boundary
            when the user switches tabs so a recovered tab doesn't
            inherit the crash from another. */}
        <PanelBoundary key={shownTab} name={TAB_LABEL[shownTab]}>
          {renderTabContent()}
        </PanelBoundary>
      </div>

      {tabPlacement === 'bottom' && (
        <LessonBottomTabs activeTab={activeTab} onTabChange={setActiveTab} />
      )}
    </div>
  );
}
